import { PlatoAssistant } from '@plato/js/react';
import type { ConsolePreviewSession } from '@plato/shared';
import { useRef, useState } from 'react';
import { ActionError, useAction } from '../components/action.tsx';
import { Loaded } from '../components/page.tsx';
import { useConsole, useTenantQuery } from '../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../data/console-data.ts';

const loadAccounts = (data: ConsoleData, tenant: TenantSummary) => data.listTestAccounts(tenant.id);

export function TryItPanel(props: { onClose: () => void; hidden: boolean }) {
  const accounts = useTenantQuery(loadAccounts);
  const { data, tenant } = useConsole();
  const [account, setAccount] = useState('');
  const [session, setSession] = useState<ConsolePreviewSession | null>(null);
  const action = useAction();

  return (
    <aside className="try-it-panel" aria-labelledby="try-it-title" hidden={props.hidden}>
      <div className="row try-it-head">
        <h2 id="try-it-title">Try it</h2>
        <button type="button" className="quiet" onClick={props.onClose}>
          Close
        </button>
      </div>
      <p className="hint">
        Chat as your users will, with your current tools and instructions. These chats are previews:
        they stay out of your users' history and your clients' credits.
      </p>
      <Loaded query={accounts}>
        {(list) => (
          <form
            className="row wrap inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              void action.run(async () => {
                setSession(null);
                setSession(
                  await data.openPreviewSession(tenant.id, account === '' ? null : account),
                );
              });
            }}
          >
            <label>
              Act as
              <select value={account} onChange={(e) => setAccount(e.target.value)}>
                <option value="">Nobody (no access to your product)</option>
                {list.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" className="primary" disabled={action.pending}>
              {session === null ? 'Start' : 'Start again'}
            </button>
          </form>
        )}
      </Loaded>
      <ActionError error={action.error} />
      {session !== null && (
        <>
          {!session.vendor_tools && (
            <p className="hint">This chat has no access to your product's tools.</p>
          )}
          <PreviewChat
            key={session.client_secret}
            session={session}
            renew={() => data.openPreviewSession(tenant.id, account === '' ? null : account)}
          />
        </>
      )}
    </aside>
  );
}

function PreviewChat(props: {
  session: ConsolePreviewSession;
  renew: () => Promise<ConsolePreviewSession>;
}) {
  const first = useRef<string | null>(props.session.client_secret);
  return (
    <PlatoAssistant
      publishableKey={props.session.publishable_key}
      embedOrigin={props.session.embed_origin}
      mode="inline"
      preview
      getClientSecret={async () => {
        const secret = first.current;
        first.current = null;
        return secret ?? (await props.renew()).client_secret;
      }}
      style={{
        display: 'block',
        flex: '1 1 0',
        minHeight: '24rem',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius)',
        overflow: 'hidden',
      }}
    />
  );
}
